import React, { useState } from 'react'
import { Document, Page } from 'react-pdf'
import type { QuoteFile } from '../../types'
import Paper from '../Paper'
import configurePdfWorker from '../FileViewer/pdfWorker'

configurePdfWorker()

/**
 * Set on every element that is still drawing PDF content. A quote's PDF render waits until none
 * remain (`usePdfReady` in customer-view) — that gate otherwise only watches `<img>`, and a
 * react-pdf page is a `<canvas>` (#24878).
 */
export const RENDER_PENDING_ATTR = 'data-render-pending'

const pendingProps = { [RENDER_PENDING_ATTR]: 'true' }

export interface S3PdfPagesProps {
  file: QuoteFile
  /** Zero-based page indexes to draw. */
  pages: number[]
  pageClassName?: string
  canvasClassName?: string
  /** Shown when the PDF cannot be fetched or parsed. */
  fallback: React.ReactNode
  onClick?: (ev: React.MouseEvent) => void
}

function S3PdfPage({
  pageNumber,
  className,
  canvasClassName
}: {
  pageNumber: number
  className?: string
  canvasClassName?: string
}) {
  const [settled, setSettled] = useState(false)
  const settle = () => setSettled(true)

  return (
    <Paper className={className} {...(settled ? {} : pendingProps)}>
      <Page
        pageNumber={pageNumber}
        className={canvasClassName}
        renderTextLayer={false}
        renderAnnotationLayer={false}
        onRenderSuccess={settle}
        onRenderError={settle}
        onLoadError={settle}
      />
    </Paper>
  )
}

/**
 * Draws the pages of an S3-hosted PDF with react-pdf. An S3 PDF has no `cloudinaryPublicId`, so the
 * Cloudinary `.png` page transform the other branch uses would request `undefined.png` (#24878).
 */
export default function S3PdfPages({
  file,
  pages,
  pageClassName,
  canvasClassName,
  fallback,
  onClick
}: S3PdfPagesProps) {
  // `src` comes from user-uploaded file records, so only an https URL reaches pdf.js. Mirrors
  // FileViewerPdfThumbnail.
  if (!file.src?.startsWith('https://')) {
    return <>{fallback}</>
  }

  return (
    <div onClick={onClick}>
      <Document
        file={file.src}
        loading={<div {...pendingProps} />}
        error={<>{fallback}</>}
        onLoadError={(error) => console.error(`FilePreview: failed to load PDF ${file.src}`, error)}
        onSourceError={(error) => console.error(`FilePreview: failed to fetch PDF ${file.src}`, error)}
      >
        {pages.map((page) => (
          // Keyed by src too: a new file under the same page index must start pending again, not
          // inherit the old page's settled state.
          <S3PdfPage
            key={`${file.src}-${page}`}
            pageNumber={page + 1}
            className={pageClassName}
            canvasClassName={canvasClassName}
          />
        ))}
      </Document>
    </div>
  )
}
